import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import '../assets/styles/LinkPage.css';
import Sidebar from '../components/Sidebar';
import { useNavigate } from 'react-router-dom';
import { 
  FaChevronDown, FaClipboard, FaLightbulb, FaLink, FaPlus, 
  FaCheck, FaTwitter, FaFacebookF, FaLinkedinIn, FaCopy,
  FaWhatsapp, FaFileAlt, FaChartLine, FaExclamationTriangle
} from 'react-icons/fa';

const LinkPage = () => {
  const [links, setLinks] = useState([]);
  const [showDescription, setShowDescription] = useState(false);
  const [copyToast, setCopyToast] = useState({ show: false, message: '' });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/');
      return;
    }

    const fetchLinks = async () => {
      try {
        setLoading(true);
        const decoded = jwtDecode(token);
        const userId = decoded.userId;
        const res = await axios.get(`https://api.startupdriver.co/api/links/${userId}`);
        setLinks(res.data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching links:', error);
        setLoading(false);
      }
    };

    fetchLinks();
  }, [navigate]);

  const handleCopyLink = (url, type) => {
    navigator.clipboard.writeText(url)
      .then(() => {
        setCopyToast({ 
          show: true, 
          message: `${type} link copied to clipboard!` 
        });
        
        setTimeout(() => {
          setCopyToast({ show: false, message: '' });
        }, 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  const handleShareOnSocial = (platform, url) => {
    let shareUrl;
    
    switch(platform) {
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent('Check out this startup form!')}`;
        break;
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent('Check out this startup form: ' + url)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const shortenUrl = (url) => {
    // Remove http:// or https:// and trim to a reasonable length
    return url.replace(/^https?:\/\//, '').substring(0, 30) + (url.length > 30 ? '...' : '');
  };

  const navigateToCreateForm = () => {
    navigate('/manage-form');
  };

  return (
    <div className="main-page">
      <Sidebar />
      <div className="content">
        <div className="link-page">
          <div className="link-page-header">
            <h2 className="link-head">Your Forms & Analytics</h2>
            
            <div className="info-section">
              <button 
                className={`info-toggle ${showDescription ? 'open' : ''}`}
                onClick={() => setShowDescription(!showDescription)}
              >
                <FaChevronDown /> {showDescription ? 'Hide info' : 'Show info'}
              </button>
              
              {showDescription && (
                <div className={`info-description open`}>
                  <div className="info-content">
                    <div className="info-item">
                      <div className="info-icon">
                        <FaLightbulb />
                      </div>
                      <div className="info-text">
                        Here you can access all your forms and their corresponding startup ranking pages.
                      </div>
                    </div>
                    
                    <div className="info-item">
                      <div className="info-icon">
                        <FaChartLine />
                      </div>
                      <div className="info-text">
                        The startup ranking page contains analysis of all data submitted by entrepreneurs.
                      </div>
                    </div>
                    
                    <div className="info-item">
                      <div className="info-icon">
                        <FaClipboard />
                      </div>
                      <div className="info-text">
                        Share the form link with entrepreneurs to collect their data.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {loading ? (
            <div className="loading-state">Loading your links...</div>
          ) : links.length > 0 ? (
            <div className="links-container">
              {links.map((link, index) => (
                <div className="link-card" key={index}>
                  <div className="card-header">
                    <div className="card-number">{index + 1}</div>
                   
                    <div className="card-date">Created: {formatDate(link.createdAt || new Date())}</div>
                  </div>
                  
                  <div className="card-content">
                    <div className="link-detail">
                      <div className="link-icon">
                        <FaFileAlt />
                      </div>
                      <div className="link-info">
                        <div className="link-label">Form Page</div>
                        <div className="link-url">{shortenUrl(link.formLink)}</div>
                      </div>
                      <button 
                        className="copy-btn"
                        onClick={() => handleCopyLink(link.formLink, 'Form')}
                        title="Copy form link"
                      >
                        <FaCopy />
                      </button>
                    </div>
                    
               
                    
                    <div className="card-actions">
                      <a 
                        href={link.formLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="action-btn form-btn"
                      >
                        <FaFileAlt /> View Form
                      </a>
                      <a 
                        href={link.analyticsLink} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="action-btn ranking-btn"
                      >
                        <FaChartLine /> View Rankings
                      </a>
                    </div>
                  </div>
                  
                  <div className="card-footer">
                    <div className="share-text">Share Form:</div>
                    <div className="share-buttons">
                      <button 
                        className="share-btn" 
                        onClick={() => handleShareOnSocial('twitter', link.formLink)}
                        title="Share on Twitter"
                      >
                        <FaTwitter />
                      </button>
                      <button 
                        className="share-btn" 
                        onClick={() => handleShareOnSocial('facebook', link.formLink)}
                        title="Share on Facebook"
                      >
                        <FaFacebookF />
                      </button>
                      <button 
                        className="share-btn" 
                        onClick={() => handleShareOnSocial('linkedin', link.formLink)}
                        title="Share on LinkedIn"
                      >
                        <FaLinkedinIn />
                      </button>
                      <button 
                        className="share-btn" 
                        onClick={() => handleShareOnSocial('whatsapp', link.formLink)}
                        title="Share on WhatsApp"
                      >
                        <FaWhatsapp />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">
                <FaLink />
              </div>
              <h3 className="empty-title">No forms found</h3>
              <p className="empty-description">
                You haven't created any forms yet. Create a form to start collecting and analyzing startup data.
              </p>
              <button className="create-form-btn" onClick={navigateToCreateForm}>
                <FaPlus /> Create Your First Form
              </button>
            </div>
          )}
          
          {copyToast.show && (
            <div className="toast show">
              <FaCheck className="toast-icon" />
              {copyToast.message}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LinkPage;